import { IdleTimer } from '@/3d/engine/IdleTimer';

const PLATFORM_IDLE_WAIT_MS = 30_000;
const ACTIVITY_EVENT_NAMES = [
  'pointerdown',
  'pointermove',
  'wheel',
  'keydown',
  'touchstart',
] as const;

/** Platform-wide idle + visibility signal for analytics active-time tracking.
 * Separate from the 3D engine's canvas-scoped `IdleTimer`; combines idle state
 * with tab visibility so "active" means not-idle AND visible. */
class PlatformIdleTimer extends EventTarget {
  private readonly idleTimer: IdleTimer;
  private idle = false;
  private visible = document.visibilityState === 'visible';
  private lastActive: boolean;

  constructor(waitMs = PLATFORM_IDLE_WAIT_MS) {
    super();
    this.idleTimer = new IdleTimer({ waitMs });
    this.lastActive = this.computeActive();

    this.idleTimer.addEventListener('idle', () => {
      this.idle = true;
      if (import.meta.env.DEV) console.debug('[platformIdleTimer] idle');
      this.emitIfChanged();
    });
    this.idleTimer.addEventListener('exitidle', () => {
      this.idle = false;
      if (import.meta.env.DEV) console.debug('[platformIdleTimer] exitidle');
      this.emitIfChanged();
    });

    const onActivity = () => this.idleTimer.reset();
    for (const eventName of ACTIVITY_EVENT_NAMES) {
      document.addEventListener(eventName, onActivity, {
        capture: true,
        passive: true,
      });
    }

    document.addEventListener(
      'visibilitychange',
      () => {
        this.visible = document.visibilityState === 'visible';
        this.emitIfChanged();
      },
      { capture: true }
    );
  }

  private computeActive() {
    return !this.idle && this.visible;
  }

  private emitIfChanged() {
    const active = this.computeActive();
    if (active === this.lastActive) return;
    this.lastActive = active;
    this.dispatchEvent(new Event(active ? 'active' : 'inactive'));
  }

  /** Whether the platform is currently considered active (not idle, tab visible). */
  isActive() {
    return this.computeActive();
  }
}

/** Singleton - the platform has one idle/visibility signal, not one per component. */
export const platformIdleTimer = new PlatformIdleTimer();
